import { nextTick, type Plugin } from 'vue';
import { useContainer } from '@kiplin/web-common/dependency-injection';
import type { SentryInitParams } from '@kiplin/web-common/error-monitoring';
import { ErrorMonitoringDependencyTypes } from '@shared/common/error-monitoring/error-monitoring.dependency-types';
import type { DeferredErrorMonitoringService } from '@shared/common/error-monitoring/domain/service/deferred-error-monitoring.service';

export const DeferredErrorMonitoringVuePlugin: Plugin<[Omit<SentryInitParams, 'app'>]> = {
  install(app, params) {
    const service = app
      .runWithContext(useContainer)
      .get<DeferredErrorMonitoringService>(ErrorMonitoringDependencyTypes.ErrorMonitoringService);
    const previousErrorHandler = app.config.errorHandler;
    let disposed = false;
    let frameId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const onError = (event: ErrorEvent): void => {
      if (event.error || event.message) service.captureException(event.error ?? new Error(event.message));
    };
    const onRejection = (event: PromiseRejectionEvent): void => {
      service.captureException(event.reason);
    };
    const onVueError: NonNullable<typeof previousErrorHandler> = (error, instance, info) => {
      service.captureException(error, { extra: { vueInfo: info } });
      if (previousErrorHandler) previousErrorHandler(error, instance, info);
      else console.error(error);
    };
    const stopEarlyCapture = (): void => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      if (app.config.errorHandler === onVueError) app.config.errorHandler = previousErrorHandler;
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    app.config.errorHandler = onVueError;

    const initialize = async (): Promise<void> => {
      try {
        const { useSentry, SentryErrorMonitoringService } = await import('@kiplin/web-common/error-monitoring');
        if (disposed) return;

        // Let Sentry wrap the original Vue handler rather than our temporary buffer.
        app.config.errorHandler = previousErrorHandler;
        useSentry().init({ ...params, app });
        service.setDelegate(new SentryErrorMonitoringService());
        stopEarlyCapture();
      } catch (error) {
        if (!disposed && app.config.errorHandler === previousErrorHandler) app.config.errorHandler = onVueError;
        console.error('Unable to initialize error monitoring', error);
      }
    };

    // Wait for the first route's async components and Vue render, then yield a paint
    // before downloading the SDK. This promise is never awaited by navigation.
    void params.router
      .isReady()
      .catch((error: unknown) => service.captureException(error))
      .then(() => nextTick())
      .then(() => {
        if (disposed) return;
        frameId = requestAnimationFrame(() => {
          timeoutId = setTimeout(() => void initialize(), 0);
        });
      });

    app.onUnmount(() => {
      disposed = true;
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      stopEarlyCapture();
    });
  },
};
